import { defineComponent as _defineComponent } from 'vue'
import SeasonSelector from '@/components/admin/season-dropdown.vue';
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useBuilderStore } from "@/store/builder";
import { useDestinations } from "@/store/destinations";
import { useRegistrationStore } from "@/store/registration";
import { Builders, Season } from "@busy-human/hxp-library";
import { useTrips } from "@/store/trips";
import { FeatureFlags } from "@busy-human/vue-component-library";
import { useSeasons } from '@/store/seasons';
import { useShoppingBuilderStore } from '@/store/builder-shopping';
import { useSeasonBuilders } from '@/store/builder-seasons';
import { useSeasonFeature } from '@/composables/useSeasonFeature';
import { usePrompt } from '@/composables/usePrompt';
import { useFlights } from '@/store/flights';

type Props = {
	message?: string
}
type Emits = {
	(e: 'close'): void
}
type BuilderStatus = {
	status: string,
	trip: string | null,
	link: string | null

}


type BuilderStatusTable = {
	name: string,
	builderId?: string,
	builderStatus: BuilderStatus,
	shortcut: string,
	shortcutAction?: () => void
	shortcutLocation?: string
	shortcutLink?: string,
	important?: boolean
}

const STEP_TOTAL_COUNT = 9;
// eslint-disable-next-line max-lines-per-function, complexity

export default /*#__PURE__*/_defineComponent({
  __name: 'trip-assigment-popup',
  props: {
    message: {}
  },
  emits: ["close"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

/* eslint-disable max-lines */
const builderShoppingStore = useShoppingBuilderStore();
const builderSeasons = useSeasonBuilders();
const builderStore = useBuilderStore();
const tripStore = useTrips();
const destinationStore = useDestinations();
const registrationStore = useRegistrationStore();
const router = useRouter();
const seasons = useSeasons();
const flightStore = useFlights();

const seasonChecklistEnabled = useSeasonFeature(Season.Features.CHECKLIST);
// const registrationEnabled = useSeasonFeature(Season.Features.REGISTRATION);

const selectedSeason = ref();

const selectableBuilders = computed(() => builderStore.selectableBuilders);

// const showTable = ref(false);




const emit = __emit;


const buildersStatuses = ref<BuilderStatusTable[]>([]);


function routeToTrip(link: string | null) {
	emit('close');
	if (typeof link == "string") {
		router.push(link);
	}
}

function routeShortcut(link: string | undefined, builderId?: string) {
	emit('close');
	if (builderId) {
		builderStore.selectBuilder(builderId);
	}
	if (typeof link == "string") {
		router.push(link);
	}
}

const buildersEnabled = useSeasonFeature(Season.Features.BUILDERS);
async function loadSingleBuilderStatus(builder: Builders.Model & { $id: string }) {
	const record = builderStore.builderRecords[builder.$id];
	//console.log("BULDER RECORD:",record, builder.name.fullName);
	const months = [
		"January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"
	];
	const builderName = builder.name.fullName;
	const completedSteps = registrationStore.getBuilderCompletedSteps(record);
	//console.log("Completed Steps:",completedSteps.length, completedSteps);

	if (!builderName) return;

	console.log("Record", record);
	if (!record) {
		if (seasons.current?.isCurrent && buildersEnabled.value === Season.FeatureState.ENABLED) {
			let builderStatus: BuilderStatusTable;
			if(localStorage.getItem(`hxp.builderignore.${seasons.current.name}.${builder.$id}`)) {
				builderStatus = {
					name: builderName,
					builderId: builder.$id,
					builderStatus: {
						status: "Builder excluded for this season",
						trip: null,
						link: null,
					},
					shortcut: 'Add Builder to Current Season',
					shortcutAction: () => builderShoppingStore.addBuilderToCurrentSeason(builder.$id)
				};
			} else {
				builderStatus = {
					name: builderName,
					builderId: builder.$id,
					builderStatus: {
						status: "Builder status unconfirmed for this season",
						trip: null,
						link: null
					},
					important: true,
					shortcut: "Confirm Builder Status",
					shortcutAction: async () => {
						const res = await usePrompt().promptConfirm(`Will ${builderName} be going on a trip this season?`, "You can still add them to this season later.");
						if(res) await builderShoppingStore.addBuilderToCurrentSeason(builder.$id);
						else localStorage.setItem(`hxp.builderignore.${seasons.current?.name}.${builder.$id}`, `${true}`);
						// eslint-disable-next-line no-use-before-define
						await loadBuilderStatuses();
					}
				};
			}
			buildersStatuses.value.push(builderStatus);
		} else {
			const builderStatus: BuilderStatusTable = {
				name: builderName,
				builderId: builder.$id,
				builderStatus: {
					status: "Builder not available for this season",
					trip: null,
					link: null,
				},
				shortcut: ''
			};
			buildersStatuses.value.push(builderStatus);
		}

		return;
	}


	//If they don't have a zoho-builder-id they should be in the Shopping Page steps
	if (record.zohoBuilderId === undefined) {
		if (record.preferences.length !== 0) {
			const builderStatus: BuilderStatusTable = {
				name: builderName,
				builderId: builder.$id,
				builderStatus: {
					status: "Cart needs to be submitted",
					trip: null,
					link: null,
				},
				shortcut: "Go to",
				shortcutLocation: "cart",
				shortcutLink: "/shopping-cart"
			};
			buildersStatuses.value.push(builderStatus);
		} else {
			const builderStatus: BuilderStatusTable = {
				name: builderName,
				builderId: builder.$id,
				builderStatus: {
					status: "Cart Empty",
					trip: null,
					link: null,
				},
				shortcut: "Go to",
				shortcutLocation: "Trip Search",
				shortcutLink: "/shopping-home"
			};
			buildersStatuses.value.push(builderStatus);
		}

		return;
	}

	//If they've paid their deposit or are assigned to a trip they should be on the builder checklist steps
	if (record.depositPaid || record.tripAssigned) {
		if (record.status === 'canceled') {
			const builderStatus: BuilderStatusTable = {
				name: builderName,
				builderId: builder.$id,
				builderStatus: {
					status: 'Reservation Cancelled',
					trip: null,
					link: null,
				},
				shortcut: 'Contact HXP if you have any questions',
			};
			buildersStatuses.value.push(builderStatus);
		}

		else if (record.tripAssigned) {
			const MINUTES_IN_MS = 60000;

			const tripId = record.tripAssigned;
			const trip = tripStore.trips[tripId];
			if (!trip) {
				const builderStatus: BuilderStatusTable = {
					name: builderName,
					builderId: builder.$id,
					builderStatus: {
						status: `Trip Assigned:`,
						trip: null,
						link: null
					},
					shortcut: "Go to",
					shortcutLocation: "builder checklist",
					shortcutLink: "/builder/dashboard"
				};
				// eslint-disable-next-line max-depth
				if (seasonChecklistEnabled.value === Season.FeatureState.DISABLED) {
					builderStatus.shortcut = 'Checklist Unavailable';
					delete builderStatus.shortcutLocation;
					delete builderStatus.shortcutLink;
				}
				buildersStatuses.value.push(builderStatus);
				return;
			}
			const destination = destinationStore.destinations[trip.destination];

			const startDate = new Date(trip.startDate);
			const endDate = new Date(trip.endDate);

			const startTimezoneOffset = startDate.getTimezoneOffset();
			const endTimezoneOffset = endDate.getTimezoneOffset();


			const startMonth = months[startDate.getMonth()];
			const endMonth = months[endDate.getMonth()];

			const startDay = new Date(startDate.getTime() + (startTimezoneOffset * MINUTES_IN_MS)).toLocaleDateString("default", { "month": "short", "day": "numeric" });
			const endDay = new Date(endDate.getTime() + (endTimezoneOffset * MINUTES_IN_MS)).toLocaleDateString("default", { "day": "numeric" });
			let tripDate = "";

			if (startMonth === endMonth) {
				tripDate = `${destination.displayName || destination.name} ${startDay} to ${endDay}`;
			} else {
				tripDate = `${destination.displayName || destination.name} ${startDay} to ${endMonth} ${endDay}`;
			}

			const status: BuilderStatus = {
				status: `Trip Assigned:`,
				trip: tripDate,
				link: `/shopping-home/${trip.destination}`
			};

			let checklistEnabled = FeatureFlags.isFeatureEnabled("BUILDER_CHECKLIST");
			if (!checklistEnabled) {
				checklistEnabled = await FeatureFlags.isFeatureEnabled({ feature: "BUILDER_CHECKLIST", context: "beta" });
			}
			const tripFlightChanged = flightStore.builderHasFlightChanges[builder.$id];
			console.log("tripFlightChanged", tripFlightChanged);
			let builderStatus: BuilderStatusTable;

			if (seasonChecklistEnabled.value === Season.FeatureState.DISABLED) {
				builderStatus = {
					name: builderName,
					builderId: builder.$id,
					builderStatus: status,
					shortcut: 'Checklist Unavailable',
				};
			} else if (checklistEnabled && !tripFlightChanged) {
				builderStatus = {
					name: builderName,
					builderId: builder.$id,
					builderStatus: status,
					shortcut: "Go to",
					shortcutLocation: "builder checklist",
					shortcutLink: "/builder/dashboard"
				};
			} else if(checklistEnabled && tripFlightChanged) {
				builderStatus = {
					name: builderName,
					builderId: builder.$id,
					builderStatus: {
						status: "Trip Flight Change: ",
						trip: tripDate,
						link: `/shopping-home/${trip.destination}`
					},
					shortcut: "View",
					shortcutLocation: "flight information",
					shortcutLink: "/builder/flightInformation",
					important: true
				};
			} else {
				builderStatus = {
					name: builderName,
					builderId: builder.$id,
					builderStatus: status,
					shortcut: 'Checklist coming soon',
				};
			}

			buildersStatuses.value.push(builderStatus);
		}

		else if (record.onWaitlist) {
			const status: BuilderStatus = {
				status: `On Waitlist`,
				trip: null,
				link: `null`
			};

			const builderStatus: BuilderStatusTable = {
				name: builderName,
				builderId: builder.$id,
				builderStatus: status,
				shortcut: "Go to",
				shortcutLocation: "builder checklist",
				shortcutLink: "/builder/dashboard"
			};

			if (seasonChecklistEnabled.value === Season.FeatureState.DISABLED) {
				builderStatus.shortcut = 'Checklist Unavailable';
				delete builderStatus.shortcutLocation;
				delete builderStatus.shortcutLink;
			}
			buildersStatuses.value.push(builderStatus);
		}

		else {
			const builderStatus: BuilderStatusTable = {
				name: builderName,
				builderId: builder.$id,
				builderStatus: {
					status: 'Awaiting Lottery',
					trip: null,
					link: null,
				},
				shortcut: 'Lotteries nightly by Age',
				shortcutLocation: "(payment page)",
				shortcutLink: "/registration/payment"
			};
			buildersStatuses.value.push(builderStatus);
		}

		return;
	}

	//If they haven't paid their deposits but they're in Zoho, they should be in the Registration Portal Steps
	if (completedSteps.length < STEP_TOTAL_COUNT) {
		const builderStatus: BuilderStatusTable = {
			name: builderName,
			builderId: builder.$id,
			builderStatus: {
				status: "Registration not finished",
				trip: null,
				link: null,
			},
			shortcut: "Go to",
			shortcutLocation: "registration",
			shortcutLink: "/registration"
		};
		buildersStatuses.value.push(builderStatus);
	}


	else if (completedSteps.length >= STEP_TOTAL_COUNT) {
		const builderStatus: BuilderStatusTable = {
			name: builderName,
			builderId: builder.$id,
			builderStatus: {
				status: "Awaiting Deposit",
				trip: null,
				link: null,
			},
			shortcut: "Go to",
			shortcutLocation: "payment page",
			shortcutLink: "/registration/payment"
		};
		buildersStatuses.value.push(builderStatus);
	}
}

// eslint-disable-next-line max-lines-per-function
async function loadBuilderStatuses() {
	await destinationStore.waitForReady();
	await tripStore.waitForReady();
	await builderStore.waitForReady();
	await flightStore.waitForReady();

	if (selectableBuilders.value && destinationStore.loaded && tripStore.loaded && builderStore.isReady) {
		buildersStatuses.value = [];
		console.log('Update to status');
		for (const builder of selectableBuilders.value) {
			await loadSingleBuilderStatus(builder);
		};
	}
}

const hasPriorSeasonBuilders = computed(() => selectableBuilders.value.some(b => !(b.$id in builderStore.builderRecords) && !localStorage.getItem(`hxp.builderignore.${seasons.current?.name}.${b.$id}`)));

function checkHasPriorSeasonBuilders() {
	return selectableBuilders.value.some(b => 
		!(b.$id in builderStore.builderRecords) && 
		!localStorage.getItem(`hxp.builderignore.${seasons.current?.name}.${b.$id}`)
	);
}

async function tryClosePopup() {
	if(checkHasPriorSeasonBuilders() && buildersEnabled.value === Season.FeatureState.ENABLED) {
		await usePrompt().promptAck("Builders not added to Season", "You still have builders which haven't been added to the current season.\nPlease go through each builder and confirm whether or not they will be going on a trip this season.");
		// eslint-disable-next-line require-atomic-updates
		// acknowledgedPriorBuilders.value = true;
	} else {
		emit('close');
	}
}

watch(() => [selectableBuilders.value, builderStore.builderRecords], async () => {
	await loadBuilderStatuses();
}, { immediate: true, deep: true });


const __returned__ = { builderShoppingStore, builderSeasons, builderStore, tripStore, destinationStore, registrationStore, router, seasons, flightStore, seasonChecklistEnabled, selectedSeason, selectableBuilders, emit, buildersStatuses, routeToTrip, routeShortcut, buildersEnabled, STEP_TOTAL_COUNT, loadSingleBuilderStatus, loadBuilderStatuses, hasPriorSeasonBuilders, checkHasPriorSeasonBuilders, tryClosePopup, SeasonSelector, ref, computed, watch, get useRouter() { return useRouter }, get useBuilderStore() { return useBuilderStore }, get useDestinations() { return useDestinations }, get useRegistrationStore() { return useRegistrationStore }, get Builders() { return Builders }, get Season() { return Season }, get useTrips() { return useTrips }, get FeatureFlags() { return FeatureFlags }, get useSeasons() { return useSeasons }, get useShoppingBuilderStore() { return useShoppingBuilderStore }, get useSeasonBuilders() { return useSeasonBuilders }, get useSeasonFeature() { return useSeasonFeature }, get usePrompt() { return usePrompt }, get useFlights() { return useFlights } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})